import { Classes, Icon, Menu, MenuItem } from "@blueprintjs/core"
import { showContextMenu } from "modules/common/services/context-menu"
import { History } from "modules/common/components/routing/withRouter";
import { observer } from "mobx-react"
import { EmptySquare } from "modules/common/components/icons/square"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { routes } from "modules/common/routes"
import internalFunc from "modules/main/functionalities"
import { can, canAny } from "modules/session/auth/access"
import { SessionType } from "modules/session/auth/models/session"
import func from "modules/session/functionalities"
import React from "react"
import { RouteComponentProps, withRouter } from "modules/common/components/routing/withRouter";
import styles from "./User.module.scss"
import Profile from "./Profile"
import { EmployerType } from "modules/spending/employee/models/employer"
import { FeedbackButton } from "modules/feedback/components/FeedbackButton"

/** Ширина блока пользователя для вкладок страниц: они лежат под ним и должны его обходить */
const HEADER_USER_WIDTH_VAR = "--header-user-width"
/** Зазор между последней вкладкой и блоком пользователя */
const HEADER_USER_GAP = 8

class Me extends React.Component<UserProps & RouteComponentProps, UserState> {
  private rootRef = React.createRef<HTMLDivElement>()
  private resizeObserver: any = null

  constructor(props: UserProps & RouteComponentProps) {
    super(props)

    this.state = {
      showProfile: false,
      employer: null,
    }
  }

  componentDidMount() {
    this.publishWidth()

    // ширина меняется от ФИО, иконок в шапке и размера окна; ResizeObserver нет в типах TS 3.9
    const Observer = (window as any).ResizeObserver

    if (Observer && this.rootRef.current) {
      this.resizeObserver = new Observer(this.publishWidth)
      this.resizeObserver.observe(this.rootRef.current)
    } else {
      window.addEventListener("resize", this.publishWidth)
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    window.removeEventListener("resize", this.publishWidth)
    // без блока пользователя вкладки возвращаются к прежней ширине 87%
    document.documentElement.style.removeProperty(HEADER_USER_WIDTH_VAR)
  }

  publishWidth = () => {
    const root = this.rootRef.current

    if (!root) {
      return
    }

    document.documentElement.style.setProperty(HEADER_USER_WIDTH_VAR, `${root.offsetWidth + HEADER_USER_GAP}px`)
  }

  render() {
    const { store, employerStore } = this.props
    const { showProfile, employer } = this.state
    return (
      <div className={`app-user centered planr-user`} ref={this.rootRef}>
        {store.tgBotLink && (
          <a href={store.tgBotLink} title="Регистрация в чат-боте">
            <i
              className={`fa fa-telegram`}
              style={{
                fontSize: "40px",
                marginRight: "20px",
                color: "#279FDA",
              }}
              aria-hidden="true"
            />
          </a>
        )}
        <FeedbackButton />
        <div className="user-details">
          <div style={{ color: "#00273d" }}>{store.userName} </div>
          <div className="user-details-post">{store.userLogin}</div>
        </div>
        {store.userAvatar ? (
          <img src={store.userAvatar} alt={"avatar"} className={styles.avatarImage} />
        ) : (
          <Icon icon="user" iconSize={44} />
        )}

        <GeneralIcon
          type="general-settings-dots"
          className={`app-user centered planr-settingsicon`}
          onClick={this.showMenu}
          size={50}
        />

        {employer && (
          <Profile
            employer={employerStore}
            show={showProfile}
            onClose={() => {
              this.setState({ showProfile: !this.state.showProfile, employer: null })
            }}
            onSave={() => {
              this.setState({ showProfile: !this.state.showProfile, employer: null })
            }}
          />
        )}
      </div>
    )
  }

  showMenu = (e: React.MouseEvent<SVGSVGElement>) => {
    const menu = this.renderContextMenu()
    showContextMenu({ content: menu, targetOffset: { left: e.clientX, top: e.clientY + 12 } })
  };

  public renderContextMenu() {
    return (
      <UserMenu
        {...this.props}
        showProfile={this.state.showProfile}
        openProfile={async () => {
          const res = await this.props.employerStore.loadProfile(this.props.store.userId)
          if (res) this.setState({ showProfile: true, employer: this.props.employerStore })
        }}
      />
    )
  }
}

export const ACCESS_MENU_LABEL = "Настройки доступа"

const UserMenu = observer(
  class extends React.Component<UserMenuProps> {
    render() {
      const { access } = this.props.store

      return (
        <Menu className={Classes.ELEVATION_1} style={{ fontFamily: "GothamPro, Arial, sans-serif" }}>
          {can(internalFunc.INTERNAL_USER_ACCESS, access) && (
            <MenuItem icon={<Icon icon="user" />} onClick={this.changeProfile} text="Редактирование профиля" />
          )}
          {can(func.SETTINGS_EDIT, access) && (
            <MenuItem icon={<Icon icon="cog" />} onClick={this.gotoSettings} text="Настройки приложения" />
          )}

          {canAny([internalFunc.INTERNAL_USER_ACCESS, func.ACCESS_EDIT], access) && (
            <MenuItem
              icon="lock"
              onClick={this.gotoAccess}
              text={can(func.ACCESS_EDIT, access) ? ACCESS_MENU_LABEL : "Таблица разрешений"}
            />
          )}

          {can(func.TOKENS_CONFIRMATION, access) && (
            <MenuItem icon="desktop" onClick={this.gotoTokens} text="Подтверждение устройств" />
          )}

          <MenuItem icon={<EmptySquare />} onClick={this.logOut} text="Выход" />
        </Menu>
      )
    }

    gotoAccess = () => {
      this.props.history.push(routes.access)
    };

    gotoSettings = () => {
      this.props.history.push(routes.settings)
    };

    gotoTokens = () => {
      this.props.history.push(routes.tokens.outer)
    };

    changeProfile = async () => {
      this.props.openProfile()
    };

    logOut = async () => {
      if (await this.props.store.logOut()) {
        this.props.onLogout()
      }
    };
  }
)

interface UserMenuProps {
  store: SessionType
  history: History
  onLogout: () => void
  openProfile: () => void
  showProfile: boolean
}

interface UserProps {
  store: SessionType
  onLogout: () => void
  employerStore: EmployerType
}

interface UserState {
  showProfile: boolean
  employer: EmployerType | null
}

export const User = withRouter(observer(Me))
